import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { classLabel } from './contexts';
import { SCHOOL_TIME_ZONE, addDays, isoWeekday, timeOf } from './dates';
import { must, useDb } from './db';
import type { LessonItem } from './schedule';

/**
 * «Діти за кордоном»: a student who lives abroad and learns in the school at a distance — all the
 * subjects, or the Ukrainian-studies component only (the rest in the school of the country). The
 * school sets the form; the family keeps the country, the city, the time zone and the hours of the
 * local school. The app shows the week by the family's clock: which lessons can be joined live and
 * which fall into the night or the local school day (those are learnt from the materials).
 */
export type AbroadTrack = Database['public']['Enums']['abroad_track'];

export interface Country {
  code: string;
  name: string;
  /** the time zones families choose from; the first is the default */
  zones: ReadonlyArray<{ id: string; name: string }>;
}

const one = (id: string, name: string): Country['zones'] => [{ id, name }];

/** The countries with Ukrainian children (НМТ was held in all of these in 2026), by name. */
export const ABROAD_COUNTRIES: readonly Country[] = [
  { code: 'AU', name: 'Австралія', zones: one('Australia/Sydney', 'Сідней') },
  { code: 'AT', name: 'Австрія', zones: one('Europe/Vienna', 'Відень') },
  { code: 'AZ', name: 'Азербайджан', zones: one('Asia/Baku', 'Баку') },
  { code: 'BE', name: 'Бельгія', zones: one('Europe/Brussels', 'Брюссель') },
  { code: 'BG', name: 'Болгарія', zones: one('Europe/Sofia', 'Софія') },
  { code: 'GB', name: 'Велика Британія', zones: one('Europe/London', 'Лондон') },
  { code: 'AM', name: 'Вірменія', zones: one('Asia/Yerevan', 'Єреван') },
  { code: 'GR', name: 'Греція', zones: one('Europe/Athens', 'Афіни') },
  { code: 'GE', name: 'Грузія', zones: one('Asia/Tbilisi', 'Тбілісі') },
  { code: 'DK', name: 'Данія', zones: one('Europe/Copenhagen', 'Копенгаген') },
  { code: 'EE', name: 'Естонія', zones: one('Europe/Tallinn', 'Таллінн') },
  { code: 'IL', name: 'Ізраїль', zones: one('Asia/Jerusalem', 'Єрусалим') },
  { code: 'IE', name: 'Ірландія', zones: one('Europe/Dublin', 'Дублін') },
  {
    code: 'ES',
    name: 'Іспанія',
    zones: [
      { id: 'Europe/Madrid', name: 'Мадрид' },
      { id: 'Atlantic/Canary', name: 'Канарські острови' },
    ],
  },
  { code: 'IT', name: 'Італія', zones: one('Europe/Rome', 'Рим') },
  {
    code: 'CA',
    name: 'Канада',
    zones: [
      { id: 'America/Toronto', name: 'Торонто, Монреаль' },
      { id: 'America/Vancouver', name: 'Ванкувер' },
      { id: 'America/Edmonton', name: 'Калгарі, Едмонтон' },
      { id: 'America/Winnipeg', name: 'Вінніпег' },
      { id: 'America/Halifax', name: 'Галіфакс' },
    ],
  },
  { code: 'CY', name: 'Кіпр', zones: one('Asia/Nicosia', 'Нікосія') },
  { code: 'LV', name: 'Латвія', zones: one('Europe/Riga', 'Рига') },
  { code: 'LT', name: 'Литва', zones: one('Europe/Vilnius', 'Вільнюс') },
  { code: 'MD', name: 'Молдова', zones: one('Europe/Chisinau', 'Кишинів') },
  { code: 'NL', name: 'Нідерланди', zones: one('Europe/Amsterdam', 'Амстердам') },
  { code: 'DE', name: 'Німеччина', zones: one('Europe/Berlin', 'Берлін') },
  { code: 'NO', name: 'Норвегія', zones: one('Europe/Oslo', 'Осло') },
  { code: 'AE', name: 'ОАЕ', zones: one('Asia/Dubai', 'Дубай') },
  { code: 'PL', name: 'Польща', zones: one('Europe/Warsaw', 'Варшава') },
  { code: 'PT', name: 'Португалія', zones: one('Europe/Lisbon', 'Лісабон') },
  { code: 'RO', name: 'Румунія', zones: one('Europe/Bucharest', 'Бухарест') },
  { code: 'SK', name: 'Словаччина', zones: one('Europe/Bratislava', 'Братислава') },
  { code: 'SI', name: 'Словенія', zones: one('Europe/Ljubljana', 'Любляна') },
  {
    code: 'US',
    name: 'США',
    zones: [
      { id: 'America/New_York', name: 'Нью-Йорк (східний час)' },
      { id: 'America/Chicago', name: 'Чикаго (центральний час)' },
      { id: 'America/Denver', name: 'Денвер (гірський час)' },
      { id: 'America/Los_Angeles', name: 'Лос-Анджелес (тихоокеанський час)' },
    ],
  },
  { code: 'TR', name: 'Туреччина', zones: one('Europe/Istanbul', 'Стамбул') },
  { code: 'HU', name: 'Угорщина', zones: one('Europe/Budapest', 'Будапешт') },
  { code: 'FI', name: 'Фінляндія', zones: one('Europe/Helsinki', 'Гельсінкі') },
  { code: 'FR', name: 'Франція', zones: one('Europe/Paris', 'Париж') },
  { code: 'HR', name: 'Хорватія', zones: one('Europe/Zagreb', 'Загреб') },
  { code: 'CZ', name: 'Чехія', zones: one('Europe/Prague', 'Прага') },
  { code: 'CH', name: 'Швейцарія', zones: one('Europe/Zurich', 'Цюрих') },
  { code: 'SE', name: 'Швеція', zones: one('Europe/Stockholm', 'Стокгольм') },
];

export const countryOf = (code: string) => ABROAD_COUNTRIES.find((c) => c.code === code);

/** 🇵🇱 from «PL». */
export function flagOf(code: string): string {
  return /^[A-Z]{2}$/.test(code)
    ? String.fromCodePoint(...[...code].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
    : '🌍';
}

/**
 * The Ukrainian-studies component (МОН): what a child abroad keeps learning in the Ukrainian school
 * while the rest is learnt in the school of the country. «Історія» of 5–6 grades is the integrated
 * course of Ukrainian history; «Географія» in 8–9 is the geography of Ukraine.
 */
export const UKRAINIAN_STUDIES: readonly string[] = [
  'Українська мова',
  'Українська література',
  'Історія України',
  'Історія',
  'Географія',
  'Географія України',
  'Основи правознавства',
  'Захист України',
  // primary school: the Ukrainian part of the integrated course
  'Я досліджую світ',
];

export const isUkrainianStudies = (subject: string) => UKRAINIAN_STUDIES.includes(subject);

/** Official pages for families abroad. */
export const ABROAD_LINKS = {
  mon: 'https://mon.gov.ua/ukrainskym-hromadianam-za-kordonom/zahalna-serednia-osvita-ukrainskym-hromadianam-za-kordonom/osvitni-mozhlyvosti-dlia-ditei-za-kordonom',
  studies: 'https://ukrainian.studies.mon.gov.ua/',
  nmt: 'https://testportal.gov.ua/',
} as const;

// ---------------------------------------------------------------------------------------------
// Clocks
// ---------------------------------------------------------------------------------------------

/** The date, the weekday and the minute of the day of a moment on a clock. */
export function clockOf(timestamp: string | Date, timeZone: string) {
  const at = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const date = new Intl.DateTimeFormat('en-CA', { timeZone }).format(at);
  const [h, m] = timeOf(at.toISOString(), timeZone).split(':').map(Number);
  // some engines write midnight as 24:00
  const minutes = ((h ?? 0) % 24) * 60 + (m ?? 0);
  return { date, weekday: isoWeekday(date), minutes };
}

/** How many hours the family's clock is ahead of Kyiv (negative — behind), e.g. -1 for Warsaw. */
export function hoursFromKyiv(timeZone: string, at: Date = new Date()): number {
  const wall = (zone: string) => {
    const c = clockOf(at, zone);
    return Date.parse(`${c.date}T00:00:00Z`) + c.minutes * 60_000;
  };
  return Math.round((wall(timeZone) - wall(SCHOOL_TIME_ZONE)) / 1_800_000) / 2;
}

/** «Київ попереду на 1 год»: the i18n key and the hours, for `t(key, { hours })`. */
export function zoneShiftOf(timeZone: string, at: Date = new Date()) {
  const hours = hoursFromKyiv(timeZone, at);
  return hours === 0
    ? { key: 'abroad.zoneSame' as const, hours: 0 }
    : hours < 0
      ? { key: 'abroad.zoneBehind' as const, hours: -hours }
      : { key: 'abroad.zoneAhead' as const, hours };
}

/** «🇵🇱 Краків» (or the country when the family did not name the city). */
export const placeOf = (h: { country: string; city: string | null }) =>
  `${flagOf(h.country)} ${h.city ?? countryOf(h.country)?.name ?? h.country}`;

/** The family's night: lessons that start before 7:00 or end after 21:00 are not for a child. */
export const FAMILY_NIGHT = { from: 21 * 60, to: 7 * 60 } as const;

/**
 * How a lesson fits the family's day: «live» — the time suits (an online lesson can be joined);
 * «school» — the child is at the local school then (weekdays); «night» — too early or too late.
 */
export type LessonFit = 'live' | 'school' | 'night';

const minutesOfTime = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

export function lessonFit(
  lesson: { startsAt: string; endsAt: string },
  home: Pick<AbroadHome, 'timezone' | 'localFrom' | 'localTo'>,
): LessonFit {
  const start = clockOf(lesson.startsAt, home.timezone);
  const end = clockOf(lesson.endsAt, home.timezone);
  if (
    start.date !== end.date ||
    start.minutes < FAMILY_NIGHT.to ||
    end.minutes > FAMILY_NIGHT.from
  ) {
    return 'night';
  }
  if (home.localFrom && home.localTo && start.weekday <= 5) {
    const from = minutesOfTime(home.localFrom);
    const to = minutesOfTime(home.localTo);
    if (start.minutes < to && end.minutes > from) return 'school';
  }
  return 'live';
}

/**
 * The coming school weeks (Mon–Fri) with their lessons, and the first one that has any — so that
 * a family does not open an empty week in the holidays.
 */
export function weeksAhead(lessons: ReadonlyArray<LessonItem>, monday: string, count = 3) {
  const weeks = Array.from({ length: count }, (_, i) => {
    const from = addDays(monday, 7 * i);
    const to = addDays(from, 4);
    return {
      from,
      to,
      lessons: lessons.filter((l) => l.date >= from && l.date <= to && l.status !== 'cancelled'),
    };
  });
  return {
    weeks,
    first: Math.max(
      0,
      weeks.findIndex((w) => w.lessons.length > 0),
    ),
  };
}

// ---------------------------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------------------------

export interface AbroadHome {
  studentId: string;
  schoolId: string;
  track: AbroadTrack;
  country: string;
  city: string | null;
  timezone: string;
  /** «08:00» — the local school day on weekdays, or nothing */
  localFrom: string | null;
  localTo: string | null;
  since: string;
}

type AbroadRow = Database['public']['Tables']['abroad_students']['Row'];

const toHome = (r: AbroadRow): AbroadHome => ({
  studentId: r.student_member_id,
  schoolId: r.school_id,
  track: r.track,
  country: r.country,
  city: r.city,
  timezone: r.timezone,
  localFrom: r.local_from?.slice(0, 5) ?? null,
  localTo: r.local_to?.slice(0, 5) ?? null,
  since: r.since,
});

/** Where the student lives, if abroad (null — learns at home in Ukraine). */
export function useAbroadHome(studentId: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['abroad', 'home', studentId],
    enabled: studentId !== '',
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<AbroadHome | null> => {
      const { data, error } = await db
        .from('abroad_students')
        .select('*')
        .eq('student_member_id', studentId)
        .maybeSingle();
      if (error) throw error;
      return data ? toHome(data) : null;
    },
  });
}

export interface AbroadStudent extends AbroadHome {
  firstName: string;
  lastName: string;
  classLabel: string | null;
}

/**
 * The students abroad one may see: all of the school for the administration, their own students
 * for a teacher (the database decides).
 */
export function useAbroadStudents(schoolId: string, enabled = true) {
  const db = useDb();
  return useQuery({
    queryKey: ['abroad', 'school', schoolId],
    enabled: enabled && schoolId !== '',
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<AbroadStudent[]> => {
      const rows = must(
        await db
          .from('abroad_students')
          .select(
            '*, student:school_members!abroad_students_school_id_student_member_id_fkey(first_name, last_name)',
          )
          .eq('school_id', schoolId),
      );
      const ids = rows.map((r) => r.student_member_id);
      const enrollments = ids.length
        ? must(
            await db
              .from('class_enrollments')
              .select(
                'student_member_id, class:classes!class_enrollments_school_id_class_id_fkey(grade, letter)',
              )
              .in('student_member_id', ids),
          )
        : [];
      const classOf = new Map(enrollments.map((e) => [e.student_member_id, classLabel(e.class)]));
      return rows
        .map((r) => ({
          ...toHome(r),
          firstName: r.student.first_name,
          lastName: r.student.last_name,
          classLabel: classOf.get(r.student_member_id) ?? null,
        }))
        .sort((a, b) => a.lastName.localeCompare(b.lastName, 'uk'));
    },
  });
}

export interface AbroadForm {
  studentId: string;
  track: AbroadTrack;
  country: string;
  city: string;
  timezone: string;
  localFrom: string | null;
  localTo: string | null;
}

export function useAbroadActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const onSettled = () => void client.invalidateQueries({ queryKey: ['abroad'] });
  return {
    /** the administration: the form of education and everything else */
    save: useMutation({
      mutationFn: async (f: AbroadForm) =>
        must(
          await db.from('abroad_students').upsert({
            school_id: schoolId,
            student_member_id: f.studentId,
            track: f.track,
            country: f.country,
            city: f.city.trim() || null,
            timezone: f.timezone,
            local_from: f.localFrom,
            local_to: f.localTo,
          }),
        ),
      onSettled,
    }),
    remove: useMutation({
      mutationFn: async (studentId: string) =>
        must(await db.from('abroad_students').delete().eq('student_member_id', studentId)),
      onSettled,
    }),
    /** the family: where they live */
    setHome: useMutation({
      mutationFn: async (f: Omit<AbroadForm, 'track'>) =>
        must(
          await db.rpc('set_abroad_home', {
            p_student: f.studentId,
            p_country: f.country,
            p_timezone: f.timezone,
            ...(f.city.trim() && { p_city: f.city.trim() }),
            ...(f.localFrom && f.localTo && { p_local_from: f.localFrom, p_local_to: f.localTo }),
          }),
        ),
      onSettled,
    }),
  };
}
